// Collect all unique tags from experiences, projects, and skills
import { workExperience, protectedFiles } from './content';
import skillsData from './skills.json';

// Function to normalize tag names
const normalizeTag = (tag) => {
  return tag.toLowerCase().trim();
};

// Tag aliases - map alternative names to canonical names
export const tagAliases = {
  "qml": "quantum machine learning",
  "quantum ml": "quantum machine learning",
  "ml": "machine learning",
  "ai": "machine learning",
  "deep learning": "machine learning",
  "quantum computing": "quantum computing",
  "quantum": "quantum computing",
  "iot": "internet of things",
  "microcontroller": "embedded systems",
  "mcu": "embedded systems",
  "web": "web development",
  "frontend": "web development",
  "front-end": "web development",
  "backend": "web development",
  "back-end": "web development",
  "scientific computing": "scientific computing",
  "numerical computing": "scientific computing",
  "numerical methods": "scientific computing"
};

// Function to get canonical tag name
export const getCanonicalTag = (tag) => {
  const normalizedTag = normalizeTag(tag);
  return tagAliases[normalizedTag] || normalizedTag;
};

// Get all unique tags
const getAllTags = () => {
  const tags = new Set();

  // Add tags from work experience
  workExperience.forEach(job => {
    job.technologies.forEach(tech => tags.add(getCanonicalTag(tech)));
  });

  // Add tags from protected files
  protectedFiles.forEach(file => {
    file.tags.forEach(tag => tags.add(getCanonicalTag(tag)));
  });

  // Add tags from skills
  skillsData.skillCategories.forEach(category => {
    category.skills.forEach(skill => tags.add(getCanonicalTag(skill.name)));
  });

  return Array.from(tags).sort();
};

// Create tag categories
export const tagCategories = {
  programming: [
    "python", 
    "c++", 
    "java", 
    "javascript", 
    "c", 
    "ocaml",
    "html",
    "css"
  ],
  frameworks: [
    "react",
    "django",
    "tensorflow",
    "pytorch",
    "qiskit",
    "pennylane",
    "node.js",
    "sparsespace",
    "sg++"
  ],
  hardware: [
    "esp32",
    "arduino",
    "raspberry pi",
    "embedded systems",
    "vhdl",
    "zigbee",
    "mpu-6050",
    "emg amplifier"
  ],
  concepts: [
    "machine learning",
    "quantum computing",
    "quantum machine learning",
    "scientific computing",
    "internet of things",
    "density estimation",
    "sparse grids",
    "high-dimensional data",
    "numerical methods",
    "data preprocessing",
    "real-time processing"
  ],
  tools: [
    "git",
    "latex",
    "platformio",
    "websocket",
    "office",
    "ltspice",
    "wireshark",
    "mqtt",
    "ntp",
    "uvicorn"
  ],
  databases: [
    "sqlite",
    "sql"
  ],
  domains: [
    "industrial applications",
    "optimization",
    "data mining",
    "scientific applications",
    "big data",
    "electrical circuits",
    "manufacturing tools",
    "contextual bandits",
    "tremor detection",
    "signal processing",
    "web development"
  ]
};

// Create relationships between tags
export const tagRelationships = {
  "quantum computing": ["qiskit", "pennylane", "optimization", "python", "quantum machine learning"],
  "quantum machine learning": ["quantum computing", "machine learning", "qiskit", "pennylane", "python", "optimization"],
  "machine learning": ["tensorflow", "pytorch", "optimization", "python", "data mining", "big data", "quantum machine learning", "reinforcement learning"],
  "embedded systems": ["esp32", "arduino", "c++", "internet of things", "zigbee", "mqtt", "mpu-6050", "emg amplifier", "real-time processing"],
  "web development": ["react", "javascript", "html", "css", "node.js", "websocket", "django"],
  "scientific computing": ["python", "numerical methods", "sparse grids", "density estimation", "sg++"],
  "internet of things": ["mqtt", "zigbee", "embedded systems", "esp32", "arduino", "real-time processing"],
  "data mining": ["python", "big data", "machine learning", "scientific applications"],
  "industrial applications": ["optimization", "manufacturing tools", "electrical circuits", "tremor detection"],
  "hardware development": ["vhdl", "esp32", "arduino", "raspberry pi", "embedded systems", "mpu-6050", "emg amplifier"],
  "signal processing": ["data preprocessing", "real-time processing", "python", "c++"],
  "tremor detection": ["signal processing", "machine learning", "embedded systems", "real-time processing"],
  "reinforcement learning": ["machine learning", "python", "optimization", "data mining", "big data", "quantum machine learning"],
  "contextual bandits": ["machine learning", "reinforcement learning", "quantum machine learning"]
};

export const tags = getAllTags(); 